import React, { useEffect, useState } from "react";
import {
  Card,
  Form,
  Input,
  Button,
  Table,
  Modal,
  Popconfirm,
  Space,
} from "antd";
import { PageHeader } from "@ant-design/pro-layout";
import {
  DeleteOutlined,
  EditOutlined,
  HomeOutlined,
  MoneyCollectOutlined,
} from "@ant-design/icons";
import {
  getAllTaxes,
  createTax,
  updateTax,
  deleteTax,
} from "../../../network/network";
import CustomBreadcrumb from "../../../components/CustomBreadcrumb";
import NotificationWithIcon from "../../../components/NotificationWithIcon";
import { useTranslation } from "react-i18next";
import { handleApiError } from "../../../utils/errorHandler";

const TaxConfiguration = () => {
  const ADD_TAX = "Add Tax";
  const [loadingBTN, setLoadingBTN] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [isDataLoading, setIsDataLoading] = useState(false);
  const [reloadTableData, setReloadTableData] = useState(false);
  const [tableData, setTableData] = useState([]);
  const [modalTitle, setModalTitle] = useState(ADD_TAX);
  const [addTaxForm] = Form.useForm();
  const { t } = useTranslation();

  useEffect(() => {
    setIsDataLoading(true);
    getAllTaxes()
      .then((response) => {
        if (response?.data?.success === false) {
          handleApiError(response, t);
          return;
        }

        setTableData(response.data.data.taxes);
      })
      .catch((error) => handleApiError(error, t))
      .finally(() => {
        setIsDataLoading(false);
      });
  }, [reloadTableData, t]);

  const showModal = () => {
    // Show Modal
    setModalVisible(true);
    // Set Modal Title
    setModalTitle(t("tax_add"));
    // Reset Form Fields
    addTaxForm.resetFields();
  };

  const handleAPIResponse = (response, type) => {
    if (response.data.success === true) {
      setReloadTableData(!reloadTableData);
      // Success
      if (type === ADD_TAX) {
        NotificationWithIcon(
          "success",
          t("tax_added"),
          t("tax_added_successfully"),
        );
      } else {
        NotificationWithIcon(
          "success",
          t("tax_updated"),
          t("tax_updated_successfully"),
        );
      }
    } else {
      handleApiError(response, t);
    }
    // Reset Form Fields
    addTaxForm.resetFields();
    // Hide Modal
    setModalVisible(false);
  };

  const handleAddUpdate = () => {
    addTaxForm
      .validateFields()
      .then((values) => {
        // Set Loading True
        setLoadingBTN(true);
        // Sending Request to API
        if (modalTitle === ADD_TAX || modalTitle === t("tax_add")) {
          createTax(values)
            .then((response) => handleAPIResponse(response, ADD_TAX))
            .catch((error) => handleApiError(error, t))
            .finally(() => {
              // Set Loading False
              setLoadingBTN(false);
            });
        } else {
          updateTax(values)
            .then((response) => handleAPIResponse(response, null))
            .catch((error) => handleApiError(error, t))
            .finally(() => {
              // Set Loading False
              setLoadingBTN(false);
            });
        }
      })
      .catch((info) => {
        console.log("Validate Failed:", info);
      });
  };

  const handleEditModal = (id) => {
    // Find discount from DataArray
    const tax = tableData.find((tax) => tax.id === id);

    // Set Modal Title
    setModalTitle(t("tax_edit") + " : " + tax.title);

    // Show Modal
    setModalVisible(true);

    // Explicitly reset Form Fields before setting them for edit
    // This ensures the form is in a clean state before being repopulated
    addTaxForm.resetFields();

    // Set Form Fields
    addTaxForm.setFieldsValue({
      id: tax.id,
      title: tax.title,
      rate: tax.rate,
    });
  };

  const handleCancel = () => {
    // Reset Form Fields
    addTaxForm.resetFields();
    setModalVisible(false);
  };

  const handleDelete = (id) => {
    deleteTax(id)
      .then((response) => {
        if (response.data.success === true) {
          setReloadTableData(!reloadTableData);
          // Success
          NotificationWithIcon(
            "success",
            t("tax_deleted"),
            t("tax_deleted_successfully"),
          );
        } else {
          handleApiError(response, t);
        }
      })
      .catch((error) => handleApiError(error, t));
  };

  const breadcrumbRoutes = [
    {
      path: "/",
      breadcrumbName: t("home"),
      icon: <HomeOutlined />,
    },
    {
      path: "tax-configuration",
      breadcrumbName: t("tax_configuration"),
      icon: <MoneyCollectOutlined />,
    },
  ];

  const TableColumns = [
    {
      title: "#",
      key: "index",
      className: "app-table-index-column",
      render: (text, record, index) => {
        return <p>{index + 1}</p>;
      },
    },
    {
      title: t("name"),
      dataIndex: "title",
    },
    {
      title: t("value"),
      dataIndex: "rate",
      render: (rate) => {
        return <>{rate} %</>;
      },
    },
    {
      title: t("action"),
      align: "center",
      width: "100px",
      render: (record) => {
        return (
          <Space>
            <Button
              type="link"
              onClick={() => handleEditModal(record.id)}
              title={t("tax_edit")}
            >
              <EditOutlined />
            </Button>
            <Popconfirm
              title={t("warning_delete")}
              onConfirm={() => handleDelete(record.id)}
              okText={t("yes")}
              cancelText={t("no")}
              placement="topLeft"
            >
              <Button type="link" title={t("delete_entity")}>
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          </Space>
        );
      },
    },
  ];

  return (
    <>
      <Card className="app-mb-20">
        <CustomBreadcrumb menus={breadcrumbRoutes} />
        <PageHeader
          className="app-p-0"
          title={t("tax_configuration")}
          ghost={false}
          extra={[
            <Button key="open-add-tax-modal" type="primary" onClick={showModal}>
              + {t("tax_add")}
            </Button>,
          ]}
        />
      </Card>
      <Card>
        <Table
          columns={TableColumns}
          dataSource={tableData}
          loading={isDataLoading}
          rowKey="id"
          pagination={false}
        />
      </Card>
      <Modal
        open={modalVisible}
        title={modalTitle}
        onOk={handleAddUpdate}
        onCancel={handleCancel}
        maskClosable={false}
        footer={[
          <Button
            type="default"
            key="back"
            loading={loadingBTN}
            onClick={handleCancel}
          >
            {t("cancel")}
          </Button>,
          <Button
            key="submit"
            type="primary"
            loading={loadingBTN}
            onClick={handleAddUpdate}
          >
            {modalTitle === ADD_TAX || modalTitle === t("tax_add")
              ? t("tax_add")
              : t("update")}
          </Button>,
        ]}
      >
        <Form
          name="add-tax-form"
          layout="vertical"
          onFinish={handleAddUpdate}
          className="app-form-full"
          form={addTaxForm}
        >
          <Form.Item
            name="title"
            label={t("name")}
            rules={[
              {
                required: true,
                message: t("validate_name_required"),
              },
            ]}
            hasFeedback
          >
            <Input />
          </Form.Item>

          <Form.Item
            name="rate"
            label={t("value") + " (%)"}
            rules={[
              {
                required: true,
                message: t("validate_value_required"),
              },
            ]}
            hasFeedback
          >
            <Input type="number" />
          </Form.Item>

          <Form.Item name="id" className="app-hidden">
            <input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default TaxConfiguration;
